import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  LocalShipping,
  Speed,
  Schedule,
  Close,
  KeyboardArrowDown,
} from "@mui/icons-material";
import {
  getDeliveryMethodLabel,
  type DeliveryMethod,
  type DeliveryMethodOption,
} from "../../../../utils/deliveryMethodUtils";
import { updateCartItemDeliveryMethod } from "../../../../lib/cartGroupItemApis";
import {
  setOptimisticDeliveryMethod,
  clearOptimisticDeliveryMethod,
  setCart,
} from "../../../../redux/cartReducer";
import { registerDeliveryMethodChange } from "../../../../redux/cartOperationsAdapter";
import { processCartResponse } from "../../../../utils/cartResponseUtils";
import {
  UnifiedDeliveryCalculationService,
  type DeliveryInput,
  type DeliveryMethodAvailability,
} from "../../../../services/UnifiedDeliveryCalculationService";
import styles from "./DeliveryMethodSelector.module.css";

interface ProductLogistics {
  expressDelivery?: boolean;
  limitTime?: boolean;
  daysToDeliver?: number;
  weight?: number;
}

interface ProductPartner {
  deliveryFeeZone?: {
    documentId: string;
    name?: string;
  };
  deliveryLeadTimeZone?: {
    documentId: string;
    name?: string;
  };
}

interface CartItem {
  cartCustomId: string;
  product: {
    id: string;
    name: string;
    logistics?: ProductLogistics;
    partner?: ProductPartner;
  };
}

interface CartGroup {
  id: string;
  deliveryMethod?: DeliveryMethod;
}

interface DeliveryMethodSelectorProps {
  item: CartItem;
  group: CartGroup;
  disabled?: boolean;
  onAvailableDatesChange?: (dates: Date[], method: DeliveryMethod) => void;
}

const DeliveryMethodSelector: React.FC<DeliveryMethodSelectorProps> = ({
  item,
  group,
  disabled = false,
  onAvailableDatesChange,
}) => {
  const dispatch = useDispatch();
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [availableMethods, setAvailableMethods] = useState<
    ExtendedDeliveryMethodOption[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<DeliveryMethod | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  // Get location from Redux
  const destinationFeeZoneId = useSelector(
    (state: any) => state.location.deliveryFeeZone?.documentId
  );
  const destinationLeadTimeZoneId = useSelector(
    (state: any) => state.location.deliveryLeadTimeZone?.documentId
  );

  // Get current delivery method from group (not item)
  const currentMethod = group.deliveryMethod || "standard";

  // Get delivery method icon component
  const getIconComponent = (method: DeliveryMethod) => {
    switch (method) {
      case "express":
        return <Speed className={styles.methodIcon} />;
      case "rapido":
        return <Schedule className={styles.methodIcon} />;
      case "standard":
      default:
        return <LocalShipping className={styles.methodIcon} />;
    }
  };

  // Extended interface for delivery method options with date and cutoff info
  interface ExtendedDeliveryMethodOption extends DeliveryMethodOption {
    dates?: Date[];
    cutoffInfo?: {
      cutoffTime: Date;
      hoursUntilCutoff?: number;
      countdown?: string | null;
    };
  }

  // Calculate available delivery methods using UnifiedDeliveryCalculationService
  const calculateAvailableMethods = async (
    productData: any,
    destFeeZoneId: string,
    destLeadTimeZoneId: string
  ): Promise<ExtendedDeliveryMethodOption[]> => {
    try {
      // Prepare input for UnifiedDeliveryCalculationService
      const deliveryInput: DeliveryInput = {
        logistics: {
          expressDelivery: !!productData.logistics?.expressDelivery,
          limitTime: productData.logistics?.limitTime || false,
          daysToDeliver: productData.logistics?.daysToDeliver || 0,
          minDaysToDeliver: productData.logistics?.minDaysToDeliver || 0,
          weight: productData.logistics?.weight || 0,
        },
        partnerSchedule: productData.partner?.daysOpen || [],
        zones: {
          originFeeZoneId:
            productData.partner?.deliveryFeeZone?.documentId || destFeeZoneId,
          originLeadTimeZoneId:
            productData.partner?.deliveryLeadTimeZone?.documentId ||
            destLeadTimeZoneId,
          destinationFeeZoneId: destFeeZoneId,
          destinationLeadTimeZoneId: destLeadTimeZoneId,
        },
        opsLeadTime: productData.logistics?.daysToDeliver || 0,
      };

      // Mock route availability - in real implementation, this should come from API/config
      const routeAvailability: DeliveryMethodAvailability = {
        express: true, // Assume routes support express
        rapido: true, // Assume routes support rapido
        standard: true, // Standard always available
      };

      // Calculate delivery options
      const deliveryOptions =
        await UnifiedDeliveryCalculationService.calculateDeliveryOptions(
          deliveryInput,
          routeAvailability
        );

      console.log(
        `🔍 [DELIVERY-SELECTOR] Calculated delivery options for ${item.cartCustomId}:`,
        {
          item: item.cartCustomId,
          productName: item.product?.name,
          deliveryInput: {
            expressDelivery: deliveryInput.logistics.expressDelivery,
            limitTime: deliveryInput.logistics.limitTime,
            daysToDeliver: deliveryInput.logistics.daysToDeliver,
            partnerSchedule: Array.isArray(deliveryInput.partnerSchedule)
              ? `Array[${deliveryInput.partnerSchedule.length}]`
              : "Object",
            originZones: deliveryInput.zones,
          },
          calculatedOptions: {
            express: {
              available: deliveryOptions.express.available,
              deliveryDate: deliveryOptions.express.deliveryDate?.toISOString(),
              cutoffTime: deliveryOptions.express.cutoffTime?.toISOString(),
              reason: deliveryOptions.express.reason,
            },
            rapido: {
              available: deliveryOptions.rapido.available,
              availableDates: deliveryOptions.rapido.availableDates?.map((d) =>
                d.toISOString()
              ),
              reason: deliveryOptions.rapido.reason,
            },
            standard: {
              available: deliveryOptions.standard.available,
              earliestDate:
                deliveryOptions.standard.earliestDate?.toISOString(),
              reason: deliveryOptions.standard.reason,
            },
          },
        }
      );

      // Transform to ExtendedDeliveryMethodOption format
      const methods: ExtendedDeliveryMethodOption[] = [];

      // Express method
      methods.push({
        value: "express",
        label: "Entrega Express",
        available: deliveryOptions.express.available,
        reason: deliveryOptions.express.reason,
        dates: deliveryOptions.express.deliveryDate
          ? [deliveryOptions.express.deliveryDate]
          : [],
        cutoffInfo: deliveryOptions.express.cutoffTime
          ? {
              cutoffTime: deliveryOptions.express.cutoffTime,
              hoursUntilCutoff: deliveryOptions.express.hoursUntilCutoff,
              countdown: deliveryOptions.express.countdown,
            }
          : undefined,
      });

      // Rápido method
      methods.push({
        value: "rapido",
        label: "Entrega Rápida",
        available: deliveryOptions.rapido.available,
        reason: deliveryOptions.rapido.reason,
        dates: deliveryOptions.rapido.availableDates || [],
        cutoffInfo: deliveryOptions.rapido.cutoffTime
          ? {
              cutoffTime: deliveryOptions.rapido.cutoffTime,
              hoursUntilCutoff: deliveryOptions.rapido.hoursUntilCutoff,
              countdown: deliveryOptions.rapido.countdown,
            }
          : undefined,
      });

      // Standard method
      methods.push({
        value: "standard",
        label: "Entrega Estándar",
        available: deliveryOptions.standard.available,
        reason: deliveryOptions.standard.reason,
        dates: deliveryOptions.standard.earliestDate
          ? [deliveryOptions.standard.earliestDate]
          : [],
        cutoffInfo: deliveryOptions.standard.cutoffTime
          ? {
              cutoffTime: deliveryOptions.standard.cutoffTime,
              hoursUntilCutoff: deliveryOptions.standard.hoursUntilCutoff,
              countdown: deliveryOptions.standard.countdown,
            }
          : undefined,
      });

      return methods;
    } catch (error) {
      console.error("Error calculating delivery methods:", error);
      // Return basic fallback methods
      return [
        {
          value: "standard",
          label: "Entrega Estándar",
          available: true,
        },
        {
          value: "rapido",
          label: "Entrega Rápida",
          available: !!productData.logistics?.limitTime,
          reason: !productData.logistics?.limitTime
            ? "Este producto no admite entrega programada"
            : undefined,
        },
        {
          value: "express",
          label: "Entrega Express",
          available: !!productData.logistics?.expressDelivery,
          reason: !productData.logistics?.expressDelivery
            ? "Este producto no admite entrega express"
            : undefined,
        },
      ];
    }
  };

  // Load available methods when selector opens
  const loadAvailableMethods = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!destinationFeeZoneId || !destinationLeadTimeZoneId) {
        setAvailableMethods([
          {
            value: "standard",
            label: "Entrega Estándar",
            available: false,
            reason: "Ingresa tu dirección para ver métodos de entrega",
          },
          {
            value: "rapido",
            label: "Entrega Rápida",
            available: false,
            reason: "Ingresa tu dirección para ver métodos de entrega",
          },
          {
            value: "express",
            label: "Entrega Express",
            available: false,
            reason: "Ingresa tu dirección para ver métodos de entrega",
          },
        ]);
        return;
      }

      // Check if product has proper zone configuration
      if (
        !item.product.partner?.deliveryFeeZone?.documentId ||
        !item.product.partner?.deliveryLeadTimeZone?.documentId
      ) {
        setAvailableMethods([
          {
            value: "standard",
            label: "Entrega Estándar",
            available: false,
            reason: "Producto sin configuración de cobertura",
          },
          {
            value: "rapido",
            label: "Entrega Rápida",
            available: false,
            reason: "Producto sin configuración de cobertura",
          },
          {
            value: "express",
            label: "Entrega Express",
            available: false,
            reason: "Producto sin configuración de cobertura",
          },
        ]);
        return;
      }

      // Calculate available methods for this specific product using real service
      const methods = await calculateAvailableMethods(
        item.product,
        destinationFeeZoneId,
        destinationLeadTimeZoneId
      );

      // Filter out current group method (only show alternatives)
      const alternativeMethods = methods.filter(
        (method) => method.value !== currentMethod
      );

      // Log the calculated methods for debugging
      console.log(
        `🚚 [DELIVERY-SELECTOR] Calculated methods for ${item.cartCustomId}:`,
        {
          currentMethod,
          allMethods: methods.map((m) => ({
            value: m.value,
            available: m.available,
            reason: m.reason,
          })),
          alternatives: alternativeMethods.map((m) => ({
            value: m.value,
            available: m.available,
            reason: m.reason,
          })),
        }
      );

      setAvailableMethods(alternativeMethods);
    } catch (error) {
      console.error("Error loading delivery methods:", error);
      setError("Error al cargar métodos de entrega");
      setAvailableMethods([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle opening the selector
  const handleOpenSelector = () => {
    setSelectorOpen(true);
    loadAvailableMethods();
  };

  // Handle method change
  const handleMethodChange = async (newMethod: DeliveryMethod) => {
    if (isChanging) return;

    // Before changing method, notify parent about available dates for rápido
    if (newMethod === "rapido" && onAvailableDatesChange) {
      const rapidoMethod = availableMethods.find((m) => m.value === "rapido");
      if (rapidoMethod && rapidoMethod.dates && rapidoMethod.dates.length > 0) {
        console.log(
          `📤 [DELIVERY-SELECTOR] Sending available dates to CartGroup:`,
          {
            item: item.cartCustomId,
            groupId: group.id,
            method: newMethod,
            datesCount: rapidoMethod.dates.length,
            dates: rapidoMethod.dates.map((d) => ({
              iso: d.toISOString(),
              local: d.toLocaleDateString("es-PE"),
              time: d.toLocaleTimeString("es-PE"),
            })),
          }
        );
        onAvailableDatesChange(rapidoMethod.dates, newMethod);
      } else {
        console.warn(
          `⚠️ [DELIVERY-SELECTOR] No available dates found for rápido method:`,
          {
            item: item.cartCustomId,
            rapidoMethodFound: !!rapidoMethod,
            datesLength: rapidoMethod?.dates?.length || 0,
          }
        );
      }
    }

    setIsChanging(true);
    setError(null);

    // Register operation first
    const operation = registerDeliveryMethodChange(
      dispatch,
      group.id,
      item.cartCustomId,
      newMethod
    );

    try {
      // Set optimistic state
      dispatch(
        setOptimisticDeliveryMethod({
          cartCustomId: item.cartCustomId,
          deliveryMethod: newMethod,
        })
      );

      // Call backend API
      const response = await updateCartItemDeliveryMethod(
        item.cartCustomId,
        newMethod,
        destinationFeeZoneId
      );

      if (response) {
        const processedCart = processCartResponse(response);
        dispatch(setCart(processedCart));
      }

      // Clear optimistic state
      dispatch(
        clearOptimisticDeliveryMethod({ cartCustomId: item.cartCustomId })
      );

      // Close selector
      setSelectorOpen(false);
      setSelectedMethod(null);

      // Complete operation successfully
      operation.complete(true);

      console.log(
        `✅ [DELIVERY-METHOD-SELECTOR] Successfully changed delivery method for ${item.cartCustomId} from ${currentMethod} to ${newMethod}`
      );
    } catch (error) {
      console.error("Error updating delivery method:", error);
      setError("Error al cambiar método de entrega");

      // Complete operation with error
      operation.complete(false);

      // Clear optimistic state on error
      dispatch(
        clearOptimisticDeliveryMethod({ cartCustomId: item.cartCustomId })
      );
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <Box className={styles.deliveryMethodSelector}>
      {/* Expandable Delivery Options Button */}
      <Button
        variant="text"
        onClick={handleOpenSelector}
        disabled={disabled || isChanging}
        className={styles.expandableOptionsButton}
        endIcon={<KeyboardArrowDown />}
      >
        Otras opciones de envío disponibles
      </Button>

      {/* Method Selector Dialog */}
      <Dialog
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Cambiar método de entrega</Typography>
            <IconButton onClick={() => setSelectorOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Método actual:{" "}
            <strong>{getDeliveryMethodLabel(currentMethod)}</strong>
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">Métodos disponibles:</FormLabel>
              <RadioGroup
                value={selectedMethod || ""}
                onChange={(e) =>
                  setSelectedMethod(e.target.value as DeliveryMethod)
                }
              >
                {availableMethods.map((method) => (
                  <FormControlLabel
                    key={method.value}
                    value={method.value}
                    disabled={!method.available || isChanging}
                    control={<Radio />}
                    label={
                      <Box sx={{ width: "100%" }}>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mb={0.5}
                        >
                          {getIconComponent(method.value)}
                          <Typography fontWeight={500}>
                            {method.label}
                          </Typography>
                        </Box>

                        {/* Show availability info */}
                        {method.available ? (
                          <Box>
                            {/* Show cutoff info if available */}
                            {method.cutoffInfo && (
                              <Typography
                                variant="caption"
                                color="primary"
                                display="block"
                              >
                                {method.cutoffInfo.countdown
                                  ? `Disponible por ${method.cutoffInfo.countdown}`
                                  : `Tiempo límite: ${method.cutoffInfo.cutoffTime.toLocaleTimeString(
                                      "es-PE",
                                      { hour: "2-digit", minute: "2-digit" }
                                    )}`}
                              </Typography>
                            )}

                            {/* Show delivery dates */}
                            {method.dates && method.dates.length > 0 && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                              >
                                {method.value === "express" && "Entrega hoy"}
                                {method.value === "rapido" &&
                                  method.dates.length === 1 &&
                                  `Entrega: ${method.dates[0].toLocaleDateString(
                                    "es-PE",
                                    {
                                      weekday: "short",
                                      day: "numeric",
                                      month: "short",
                                    }
                                  )}`}
                                {method.value === "rapido" &&
                                  method.dates.length > 1 &&
                                  `Fechas disponibles: ${method.dates.length} opciones`}
                                {method.value === "standard" &&
                                  `Desde: ${method.dates[0].toLocaleDateString(
                                    "es-PE",
                                    {
                                      weekday: "short",
                                      day: "numeric",
                                      month: "short",
                                    }
                                  )}`}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography
                            variant="caption"
                            color="error"
                            display="block"
                          >
                            {method.reason}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ))}
                {availableMethods.length === 0 && !isLoading && (
                  <Typography color="textSecondary">
                    No hay métodos alternativos disponibles
                  </Typography>
                )}
              </RadioGroup>
            </FormControl>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSelectorOpen(false)} disabled={isChanging}>
            Cancelar
          </Button>
          <Button
            onClick={() => selectedMethod && handleMethodChange(selectedMethod)}
            disabled={!selectedMethod || isChanging}
            variant="contained"
          >
            {isChanging ? <CircularProgress size={20} /> : "Cambiar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeliveryMethodSelector;
